import { useState, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError } from '@/shared/api/client'

/**
 * Настройки React Query на весь кабинет.
 *
 * Клиент создаётся внутри компонента, а не в модуле: модульный
 * синглтон переживает горячую перезагрузку и в разработке накапливает
 * состояние от предыдущих правок.
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Полминуты данные считаются свежими. Кабинет правит один
            // человек, и перезапрашивать анкету на каждый переход между
            // разделами незачем.
            staleTime: 30_000,
            // Повтор только на ошибках сервера. На 400 и 404 ответ не
            // изменится, а три повтора превращают мгновенную ошибку в
            // несколько секунд ожидания.
            retry: (failureCount, error) => {
              if (error instanceof ApiError && error.status < 500) return false
              return failureCount < 2
            },
            refetchOnWindowFocus: false,
          },
          mutations: {
            // Повторять изменяющие запросы нельзя: неизвестно, дошёл ли
            // первый. Повтор регистрации создаёт вторую компанию.
            retry: false,
          },
        },
      }),
  )

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
